import React, { useState, useEffect } from 'react';
import { Container, Box, Grid, Typography, CircularProgress, Alert, Paper, FormControl, InputLabel, Select, MenuItem, Button, Chip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import Layout from '../components/Layout';
import SectionHeader from '../components/SectionHeader';
import PropertyCard from '../components/PropertyCard';
import { PROPERTY_CATEGORIES } from '../config/categories';

const AllProperties = () => {
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('');

  // Get category from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryFromUrl = params.get('category') || '';
    setSelectedCategory(categoryFromUrl);
  }, [location.search]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await apiService.getProperties();
        
        if (response.success) {
          setProperties(response.data || []);
          setError(null);
        } else {
          setError(response.message || 'Failed to fetch properties');
          setProperties([]);
        }
      } catch (error) {
        console.error('❌ Failed to fetch properties:', error);
        setError('Error loading properties. Please try again later.');
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = properties;

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    setFilteredProperties(filtered);
  }, [properties, selectedCategory]);

  const handleClearFilters = () => {
    setSelectedCategory('');
    // Update URL in same window
    window.history.replaceState({}, '', '/all-properties');
  };

  const handleCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory);
    // Update URL in same window
    if (newCategory && newCategory !== 'all') {
      window.history.replaceState({}, '', `/all-properties?category=${newCategory}`);
    } else {
      window.history.replaceState({}, '', '/all-properties');
    }
  };

  // Get category label for header
  const getCategoryLabel = () => {
    if (!selectedCategory || selectedCategory === 'all') {
      return 'All Properties';
    }
    const category = PROPERTY_CATEGORIES.find(c => c.value === selectedCategory);
    return category ? `${category.icon} ${category.labelEn}` : 'All Properties';
  };

  return (
    <Layout>
      <Box sx={{ bgcolor: '#f9fafb', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <SectionHeader
            title={getCategoryLabel()}
            subtitle={selectedCategory ? `Explore properties in ${PROPERTY_CATEGORIES.find(c => c.value === selectedCategory)?.labelEn?.replace(/^[^\s]*\s/, '').trim()}` : "Explore our wide range of properties available for rent or sale"}
            icon="🏠"
          />
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        {/* Filters Section */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4, bgcolor: '#f7fafc', border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#2d3748' }}>
            🔍 Filters
          </Typography>

          <Grid container spacing={3}>
            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Category"
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {PROPERTY_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.labelEn?.replace(/^[^\s]*\s/, '').trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Clear Filters Button */}
          {selectedCategory && (
            <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={`Category: ${PROPERTY_CATEGORIES.find(c => c.value === selectedCategory)?.labelEn?.replace(/^[^\s]*\s/, '').trim()}`}
                onDelete={() => handleClearFilters()}
                color="primary"
                variant="outlined"
              />
              <Button 
                size="small" 
                variant="outlined" 
                onClick={handleClearFilters}
                sx={{ ml: 'auto' }}
              >
                Clear Filter
              </Button>
            </Box>
          )}
        </Paper>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ color: '#718096', fontWeight: 500 }}>
            Showing <strong>{filteredProperties.length}</strong> of <strong>{properties.length}</strong> properties
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Properties Grid */}
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredProperties.length > 0 ? (
          <Grid container spacing={3}>
            {filteredProperties.map((property) => (
              <Grid item key={property.id} xs={12} sm={6} md={4}>
                <PropertyCard property={property} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              📭 No properties found matching your filters.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try adjusting your filters to see more results.
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleClearFilters}
              sx={{ mt: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              Clear Filter
            </Button>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default AllProperties;